import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import CurrentRound from './components/CurrentRound';
import LuckyBalls from './components/LuckyBalls';
import LuckyNumberModal from './components/LuckyNumberModal';
import HistoryRounds from './components/HistoryRounds';
import * as bitcoinApi from './services/bitcoinApi';
import * as calculator from './utils/protocolCalculator';

function App() {
  const [currentHeight, setCurrentHeight] = useState(null);
  const [roundData, setRoundData] = useState(null);
  const [luckyNumbers, setLuckyNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLucky, setSelectedLucky] = useState(null);
  const [historyRounds, setHistoryRounds] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // 加载当前轮次数据
  const loadRoundData = useCallback(async () => {
    try {
      setError(null);

      // 1. 获取当前区块高度
      const height = await bitcoinApi.getCurrentBlockHeight();
      setCurrentHeight(height);

      // 2. 计算轮次信息
      const roundNumber = calculator.calculateRoundNumber(height);
      const startHeight = calculator.getRoundStartHeight(roundNumber);
      const endHeight = calculator.getRoundEndHeight(roundNumber);
      const progress = calculator.getProgressInRound(height);
      const progressPercentage = calculator.getProgressPercentage(height);
      const completedLuckyCount = calculator.getCompletedLuckyCount(height);

      // 3. 获取轮次开始区块的时间戳
      const startBlocks = await bitcoinApi.getBlocks(startHeight);
      const startBlock = startBlocks.find(b => b.height === startHeight);
      const startTime = startBlock?.timestamp;

      // 4. 预估完成时间
      const estimatedEndTime = calculator.estimateRoundCompletionTime(roundNumber, height);

      setRoundData({
        roundNumber,
        startHeight,
        endHeight,
        startTime,
        estimatedEndTime,
        progress,
        progressPercentage,
        completedLuckyCount,
      });

      // 5. 加载已完成的幸运数字
      await loadLuckyNumbers(roundNumber, height, completedLuckyCount);

      setLoading(false);
    } catch (err) {
      console.error('加载轮次数据失败:', err);
      setError(err.message || '加载数据失败，请刷新重试');
      setLoading(false);
    }
  }, []);

  // 加载幸运数字
  const loadLuckyNumbers = async (roundNumber, currentHeight, completedCount) => {
    const numbers = [];

    for (let i = 0; i < calculator.PROTOCOL_PARAMS.LUCKY_NUMBERS_PER_ROUND; i++) {
      const status = calculator.getLuckyNumberStatus(i, currentHeight, roundNumber);
      
      if (status === 'completed') {
        try {
          const range = calculator.getLuckyNumberBlockRange(roundNumber, i);
          const blocks = await bitcoinApi.getLuckyNumberBatchBlocks(range.start);
          const details = calculator.getLuckyNumberCalculationDetails(blocks);
          
          numbers.push({
            index: i,
            status,
            number: details.luckyNumber,
            blocks: details.blocks,
            concatenated: details.concatenated,
            hash: details.hash,
            lastChar: details.lastChar,
          });
        } catch (err) {
          console.error(`加载幸运数字 #${i + 1} 失败:`, err);
          numbers.push({ index: i, status: 'error' });
        }
      } else {
        numbers.push({ index: i, status });
      }
    }

    setLuckyNumbers(numbers);
  };

  // 获取指定轮次的详细信息
  const fetchRoundDetails = async (roundNum) => {
    const startHeight = calculator.getRoundStartHeight(roundNum);
    const endHeight = calculator.getRoundEndHeight(roundNum);
    
    let startTime = null;
    try {
      const startBlocks = await bitcoinApi.getBlocks(startHeight);
      const startBlock = startBlocks.find(b => b.height === startHeight);
      startTime = startBlock?.timestamp;
    } catch (e) {
      console.warn(`Failed to get start block for round ${roundNum}`, e);
    }

    const numbers = [];
    for (let i = 0; i < calculator.PROTOCOL_PARAMS.LUCKY_NUMBERS_PER_ROUND; i++) {
        try {
            const range = calculator.getLuckyNumberBlockRange(roundNum, i);
            const blocks = await bitcoinApi.getLuckyNumberBatchBlocks(range.start);
            const details = calculator.getLuckyNumberCalculationDetails(blocks);
            numbers.push({
                index: i,
                number: details.luckyNumber,
                hash: details.hash,
                blocks: details.blocks,
                concatenated: details.concatenated,
                lastChar: details.lastChar,
                status: 'completed'
            });
        } catch (e) {
            console.warn(`Failed to get lucky number ${i} for round ${roundNum}`, e);
            numbers.push({ index: i, error: true });
        }
    }
    
    return {
        roundNumber: roundNum,
        startHeight,
        endHeight,
        startTime,
        luckyNumbers: numbers
    };
  };

  // 加载历史轮次数据
  const loadHistory = async () => {
    if (!roundData || !roundData.roundNumber) return;
    
    setHistoryLoading(true);
    try {
      const currentRoundNum = roundData.roundNumber;
      let startRound;

      if (historyRounds.length > 0) {
        // 如果已有历史数据，从最后一条继续往前加载
        const lastRound = historyRounds[historyRounds.length - 1];
        startRound = lastRound.roundNumber - 1;
      } else {
        // 如果没有历史数据，从当前轮次的前一轮开始
        startRound = currentRoundNum - 1;
      }

      const newHistoryData = [];
      const BATCH_SIZE = 3; // 每次加载 3 条
      
      for (let i = 0; i < BATCH_SIZE; i++) {
        const targetRound = startRound - i;
        if (targetRound < 1) break;
        
        const roundInfo = await fetchRoundDetails(targetRound);
        newHistoryData.push(roundInfo);
      }
      
      if (newHistoryData.length > 0) {
        setHistoryRounds(prev => [...prev, ...newHistoryData]);
      }
    } catch (err) {
      console.error('加载历史数据失败:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // 处理球体点击
  const handleBallClick = (index, luckyData) => {
    console.log('点击小球:', index, luckyData);
    // 只要有详细数据（blocks），就可以显示模态框
    if (luckyData && (luckyData.status === 'completed' || luckyData.blocks)) {
      setSelectedLucky(luckyData);
    }
  };

  // 关闭模态框
  const handleCloseModal = () => {
    setSelectedLucky(null);
  };

  // 初始加载
  useEffect(() => {
    loadRoundData();
  }, [loadRoundData]);

  // 定时刷新 (每 60 秒)
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('定时刷新数据...');
      loadRoundData();
    }, 60000);

    return () => clearInterval(interval);
  }, [loadRoundData]);

  if (loading) {
    return (
      <div className="App">
        <div className="container">
          <header className="app-header">
            <div className="header-title">
              <span className="btc-icon">₿</span>
              <h1>BTC Blessed</h1>
            </div>
            <p className="header-subtitle">基于比特币区块的去中心化随机数提取协议</p>
          </header>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>加载中...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <div className="container">
          <header className="app-header">
            <div className="header-title">
              <span className="btc-icon">₿</span>
              <h1>BTC Blessed</h1>
            </div>
            <p className="header-subtitle">基于比特币区块的去中心化随机数提取协议</p>
          </header>
          <div className="error-container">
            <p className="error-message">{error}</p>
            <button className="retry-button" onClick={loadRoundData}>
              重试
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="container">
        <header className="app-header">
          <div className="header-title">
            <span className="btc-icon">₿</span>
            <h1>BTC Blessed</h1>
          </div>
          <p className="header-subtitle">基于比特币区块的去中心化随机数提取协议</p>
          {currentHeight && (
            <p className="current-height">
              当前区块高度: <span className="highlight">#{currentHeight}</span>
            </p>
          )}
        </header>

        <main>
          <CurrentRound roundData={roundData} currentHeight={currentHeight} />
          <LuckyBalls luckyNumbers={luckyNumbers} onBallClick={handleBallClick} progress={roundData?.progress || 0} />
          <HistoryRounds 
            rounds={historyRounds} 
            loading={historyLoading} 
            onLoadHistory={loadHistory} 
            onBallClick={handleBallClick}
          />
        </main>

        {selectedLucky && (
          <LuckyNumberModal luckyData={selectedLucky} onClose={handleCloseModal} />
        )}
      </div>
    </div>
  );
}

export default App;
